import {useCallback, useRef, useState} from 'react';

import {getUserMessage} from 'util/Net';

import {
  archiveRoutine as archiveApi,
  createRoutine as createApi,
  pauseRoutine as pauseApi,
  resumeRoutine as resumeApi,
  updateRoutine as updateApi,
} from './RoutinesDAO';
import {CreateRoutinePayload, UpdateRoutinePayload} from './RoutineTypes';

interface UseRoutineMutationsResult {
  createRoutine: (payload: CreateRoutinePayload) => Promise<string | null>;
  updateRoutine: (id: string, payload: UpdateRoutinePayload) => Promise<boolean>;
  pauseRoutine: (id: string) => Promise<boolean>;
  resumeRoutine: (id: string) => Promise<boolean>;
  archiveRoutine: (id: string, reason: string) => Promise<boolean>;
  isSaving: boolean;
  error: string | null;
  clearError: () => void;
}

export default function useRoutineMutations(onSuccess: () => Promise<void>): UseRoutineMutationsResult {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  async function runMutation<T>(fn: () => Promise<T>, failureValue: T): Promise<T> {
    setIsSaving(true);
    setError(null);
    try {
      const result = await fn();
      await onSuccessRef.current();
      return result;
    } catch (e) {
      const message = getUserMessage(e);
      if (!message) {
        throw e;
      }
      setError(message);
      return failureValue;
    } finally {
      setIsSaving(false);
    }
  }

  const createRoutine = useCallback(
    (payload: CreateRoutinePayload): Promise<string | null> =>
      runMutation(async () => {
        const result = await createApi(payload);
        return result.id;
      }, null),
    []
  );

  const updateRoutine = useCallback(
    (id: string, payload: UpdateRoutinePayload): Promise<boolean> =>
      runMutation(async () => {
        await updateApi(id, payload);
        return true;
      }, false),
    []
  );

  const pauseRoutine = useCallback(
    (id: string): Promise<boolean> =>
      runMutation(async () => {
        await pauseApi(id);
        return true;
      }, false),
    []
  );

  const resumeRoutine = useCallback(
    (id: string): Promise<boolean> =>
      runMutation(async () => {
        await resumeApi(id);
        return true;
      }, false),
    []
  );

  const archiveRoutine = useCallback(
    (id: string, reason: string): Promise<boolean> =>
      runMutation(async () => {
        await archiveApi(id, reason);
        return true;
      }, false),
    []
  );

  return {
    createRoutine,
    updateRoutine,
    pauseRoutine,
    resumeRoutine,
    archiveRoutine,
    isSaving,
    error,
    clearError,
  };
}
